import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AuthTypeOption,
  Provider,
} from '@/react/portainer/account/git-credentials/types';
import { isOmniCredentials } from '@/react/portainer/settings/sharedCredentials/utils';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { success as notifySuccess } from '@/portainer/services/notifications';
import { withError } from '@/react-tools/react-query';

import {
  CreateCredentialPayload,
  Credential,
  CredentialType,
  GitCredential,
  GitCredentialResponse,
  GitCredentials,
  UpdateCredentialPayload,
} from './types';

const queryKeys = {
  allCredentials: () => ['cloud-credentials'] as const,
  credential: (id: number, isGitCredential: boolean) =>
    [...queryKeys.allCredentials(), id, isGitCredential] as const,
};

function gitCredentialToCredential(credential: GitCredential): Credential {
  return {
    id: credential.id,
    created: credential.creationDate,
    name: credential.name,
    provider: CredentialType.GIT,
    credentials: {
      username: credential.username,
      personalAccessToken: '',
      provider: credential.provider ?? Provider.Custom,
      authType: credential.authorizationType ?? AuthTypeOption.Basic,
      urlPattern: credential.urlPattern ?? '',
    },
  };
}

export async function createCredential(credential: CreateCredentialPayload) {
  try {
    if (isOmniCredentials(credential.credentials)) {
      await axios.get('omni/serviceaccount/validate', {
        params: {
          endpoint: credential.credentials.endpoint,
          serviceAccountKey: credential.credentials.serviceAccountKey,
        },
      });
    }

    if (credential.provider === CredentialType.GIT) {
      const git = credential.credentials as GitCredentials;
      return await axios.post(buildSharedGitCredentialsUrl(), {
        name: credential.name,
        username: git.username,
        password: git.personalAccessToken,
        provider: git.provider,
        authorizationType: git.authType,
        urlPattern: git.urlPattern,
      });
    }

    return await axios.post<Credential>(buildUrl(), credential);
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to create credential');
  }
}

export async function getCloudCredentials() {
  try {
    const [{ data: credentials }, { data: gitCredentials }] = await Promise.all(
      [
        axios.get<Credential[]>(buildUrl()),
        axios.get<GitCredential[]>(buildSharedGitCredentialsUrl()),
      ]
    );

    return [...credentials, ...gitCredentials.map(gitCredentialToCredential)];
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to get credentials');
  }
}

export async function deleteCredential(credential: Credential) {
  try {
    if (credential.provider === CredentialType.GIT) {
      return await axios.delete(buildSharedGitCredentialsUrl(credential.id));
    }
    return await axios.delete<Credential[]>(buildUrl(credential.id));
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to delete credential');
  }
}

export async function getCloudCredential(id: number, isGitCredential = false) {
  try {
    if (isGitCredential) {
      const { data } = await axios.get<GitCredential>(
        buildSharedGitCredentialsUrl(id)
      );

      return gitCredentialToCredential(data);
    }

    const { data } = await axios.get<Credential>(buildUrl(id));

    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to get credentials');
  }
}

export async function updateCredential(
  credential: Partial<UpdateCredentialPayload>,
  id: number,
  isGitCredential = false
) {
  try {
    if (isGitCredential) {
      const git = credential.credentials as Partial<GitCredentials>;
      const { data } = await axios.put<GitCredentialResponse>(
        buildSharedGitCredentialsUrl(id),
        {
          name: credential.name,
          username: git?.username,
          password: git?.personalAccessToken,
          provider: git?.provider,
          authorizationType: git?.authType,
          urlPattern: git?.urlPattern,
        }
      );

      return data;
    }

    const { data } = await axios.put(buildUrl(id), credential);

    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to update credential');
  }
}

export function useUpdateCredentialMutation(
  id: number,
  isGitCredential = false
) {
  const queryClient = useQueryClient();

  return useMutation(
    ({ credential }: { credential: Partial<UpdateCredentialPayload> }) =>
      updateCredential(credential, id, isGitCredential),
    {
      onSuccess: (_, data) => {
        notifySuccess(
          'Credentials updated successfully',
          data.credential.name || ''
        );
        return queryClient.invalidateQueries(
          queryKeys.credential(id, isGitCredential)
        );
      },
      meta: {
        error: {
          title: 'Failure',
          message: 'Unable to update credential',
        },
      },
    }
  );
}

export function useDeleteCredentialMutation() {
  const queryClient = useQueryClient();

  return useMutation(deleteCredential, {
    onSuccess: (_, credential) => {
      notifySuccess('Credentials deleted successfully', credential.name);
      return queryClient.invalidateQueries(queryKeys.allCredentials());
    },
    meta: {
      error: {
        title: 'Failure',
        message: 'Unable to delete credential',
      },
    },
  });
}

export function useCloudCredential(
  id: number,
  authorized = true,
  isGitCredential = false
) {
  return useQuery(
    queryKeys.credential(id, isGitCredential),
    () => getCloudCredential(id, isGitCredential),
    {
      cacheTime: 0, // don't cache to make sure the Use SSH key authentication is correctly set
      enabled: id >= 0 && authorized,
      ...withError('Unable to retrieve cloud credential'),
    }
  );
}

export function useCloudCredentials(queryOptions?: {
  select?: (credentials: Credential[]) => Credential[];
}) {
  return useQuery(
    queryKeys.allCredentials(),
    () => getCloudCredentials(),
    queryOptions
  );
}

export function useCreateCredentialMutation() {
  const queryClient = useQueryClient();

  return useMutation(createCredential, {
    onSuccess: (_, payload) => {
      notifySuccess('Credentials created successfully', payload.name);
      return queryClient.invalidateQueries(queryKeys.allCredentials());
    },
    meta: {
      error: {
        title: 'Failure',
        message: 'Unable to create credential',
      },
    },
  });
}

function buildUrl(credentialId?: number) {
  let url = 'cloud/credentials';
  if (credentialId) {
    url += `/${credentialId}`;
  }
  return url;
}

function buildSharedGitCredentialsUrl(credentialId?: number) {
  let url = 'cloud/gitcredentials';
  if (credentialId) {
    url += `/${credentialId}`;
  }
  return url;
}
